/**
 * RoleWizardStepReview.jsx — Employer v1.1 Phase 0.6 Slice C Step 4.
 *
 * Read-only summary of Steps 1-3 + publish-blocker checklist + the
 * "what happens on publish" preview card + Publish CTA.
 *
 * The blocker checklist runs the client-side `assertPublishableMirror`
 * (mirror of `core.employer_role.assert_publishable`). Each blocker
 * surfaces a `Fix this →` link that deep-links back to the offending
 * step via `onJumpToStep(stepNumber)`. CTA is disabled until the
 * checklist is fully green.
 *
 * Pre-code plan §5.5 reinforced inline: criteria refinements (Step 3)
 * do NOT affect publishability.
 */
import { useMemo } from 'react';
import {
  AlertTriangle, ArrowRight, Check, CircleCheck, Info, Rocket,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { assertPublishableMirror } from '@/lib/employer/assertPublishableMirror';

function isList(x) { return Array.isArray(x) && x.length > 0; }
function isObj(x)  { return x && typeof x === 'object' && !Array.isArray(x); }

export default function RoleWizardStepReview({
  roleId,
  title,
  definition,
  criteria,
  onJumpToStep,
  onPublish,
}) {
  const blockers = useMemo(
    () => assertPublishableMirror({ title, definition }),
    [title, definition],
  );

  if (!roleId) {
    return (
      <div
        className="p-6 text-sm text-slate-300"
        data-testid="wizard-step-review-locked"
      >
        Save the draft on Step 1 before reviewing — Publish dispatches against
        the saved row.
      </div>
    );
  }

  const defn       = definition || {};
  const crit       = criteria || {};
  const compBand   = isObj(defn.comp_band) ? defn.comp_band : null;
  const responsibs = isList(defn.responsibilities)    ? defn.responsibilities    : [];
  const mustSkills = isList(defn.must_have_skills)    ? defn.must_have_skills    : [];
  const niceSkills = isList(defn.nice_to_have_skills) ? defn.nice_to_have_skills : [];

  const reqCrit    = isList(crit.required_criteria)   ? crit.required_criteria   : [];
  const prefCrit   = isList(crit.preferred_criteria)  ? crit.preferred_criteria  : [];
  const dealbreak  = isObj(crit.dealbreakers) ? crit.dealbreakers : {};
  const weights    = isObj(crit.dimension_weights) ? crit.dimension_weights : {};

  const ready = blockers.length === 0;

  return (
    <div className="space-y-6 p-6" data-testid="wizard-step-review">
      <header>
        <h2 className="text-base font-semibold text-ndor-ink">
          Review &amp; publish
        </h2>
        <p className="text-sm text-slate-300">
          Confirm the role definition. Publishing is one-way for the draft →
          active transition; close + archive remain available afterwards.
        </p>
      </header>

      {/* Blocker checklist */}
      <Card data-testid="publish-blocker-checklist">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            {ready ? (
              <CircleCheck className="h-5 w-5 text-emerald-600" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            )}
            {ready ? 'Ready to publish' : 'Publish blockers'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {ready ? (
            <p
              className="text-sm text-emerald-200"
              data-testid="publish-blockers-empty"
            >
              All required fields are filled. You can publish this role now.
            </p>
          ) : (
            <ul className="space-y-2" data-testid="publish-blockers-list">
              {blockers.map((b) => (
                <li
                  key={b.code}
                  className="flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
                  data-testid={`publish-blocker-${b.code}`}
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-200" />
                  <div className="flex-1">
                    <div className="font-medium text-ndor-ink">{b.message}</div>
                    <div className="text-xs text-slate-300">{b.fixHint}</div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onJumpToStep(b.step)}
                    data-testid={`publish-blocker-fix-${b.code}`}
                  >
                    Fix this
                    <ArrowRight className="ml-1 h-3 w-3" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Step 1-2 summary */}
      <Card data-testid="review-definition-summary">
        <CardHeader>
          <CardTitle className="text-base">Role definition</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-300">Title</dt>
              <dd className="text-slate-100" data-testid="review-title">
                {(title || '').trim() || <span className="text-slate-300">—</span>}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-300">Must-have domain</dt>
              <dd className="text-slate-100" data-testid="review-domain">
                {defn.must_have_domain || <span className="text-slate-300">—</span>}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-300">Working model</dt>
              <dd className="text-slate-100">
                {defn.working_model || <span className="text-slate-300">—</span>}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-300">Location</dt>
              <dd className="text-slate-100">
                {defn.location_constraint || <span className="text-slate-300">—</span>}
              </dd>
            </div>
            <div className="col-span-2">
              <dt className="text-xs uppercase tracking-wide text-slate-300">Compensation band</dt>
              <dd className="text-slate-100" data-testid="review-comp-band">
                {compBand
                  ? `${compBand.currency || ''} ${compBand.min ?? '?'}${compBand.max != null ? `–${compBand.max}` : ''}`.trim()
                  : <span className="text-slate-300">—</span>}
              </dd>
            </div>
          </dl>

          <div>
            <div className="mb-1 text-xs uppercase tracking-wide text-slate-300">
              Responsibilities
            </div>
            {responsibs.length > 0 ? (
              <ul
                className="list-disc pl-5 text-slate-100"
                data-testid="review-responsibilities"
              >
                {responsibs.map((r, i) => (<li key={i}>{r}</li>))}
              </ul>
            ) : (
              <div className="text-slate-300">— none yet</div>
            )}
          </div>

          <div>
            <div className="mb-1 text-xs uppercase tracking-wide text-slate-300">
              Must-have skills
            </div>
            {mustSkills.length > 0 ? (
              <div className="flex flex-wrap gap-1" data-testid="review-must-skills">
                {mustSkills.map((s, i) => (
                  <Badge key={i} className="bg-emerald-500/10 text-emerald-200 border-transparent">
                    {s}
                  </Badge>
                ))}
              </div>
            ) : (
              <div className="text-slate-300">— none yet</div>
            )}
          </div>

          <div>
            <div className="mb-1 text-xs uppercase tracking-wide text-slate-300">
              Nice-to-have skills
            </div>
            {niceSkills.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {niceSkills.map((s, i) => (
                  <Badge key={i} className="bg-sky-500/10 text-sky-200 border-transparent">
                    {s}
                  </Badge>
                ))}
              </div>
            ) : (
              <div className="text-slate-300">— none yet</div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Step 3 summary */}
      <Card data-testid="review-criteria-summary">
        <CardHeader>
          <CardTitle className="text-base">Evaluation criteria</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <p
            className="flex items-start gap-2 rounded-md bg-ndor-surface-alt p-3 text-xs text-slate-300"
            data-testid="review-criteria-disclaimer"
          >
            <Info className="mt-0.5 h-3 w-3" />
            Criteria refinements take effect at evaluation time. They do not
            change publish requirements.
          </p>

          <div>
            <div className="mb-1 text-xs uppercase tracking-wide text-slate-300">
              Dimension weights
            </div>
            <div className="grid grid-cols-2 gap-2">
              {['seniority','domain_alignment','evidence_strength','location','risk'].map((k) => (
                <div
                  key={k}
                  className="flex items-center justify-between rounded-md bg-ndor-surface-alt px-3 py-2"
                  data-testid={`review-weight-${k}`}
                >
                  <span className="text-slate-300">{k}</span>
                  <span className="font-mono text-slate-100">{weights[k] ?? 0}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-1 text-xs uppercase tracking-wide text-slate-300">
              Required criteria
            </div>
            {reqCrit.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {reqCrit.map((c, i) => (
                  <Badge key={i} className="bg-emerald-500/10 text-emerald-200 border-transparent">
                    {c}
                  </Badge>
                ))}
              </div>
            ) : (
              <div className="text-slate-300">— none</div>
            )}
          </div>

          <div>
            <div className="mb-1 text-xs uppercase tracking-wide text-slate-300">
              Preferred criteria
            </div>
            {prefCrit.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {prefCrit.map((c, i) => (
                  <Badge key={i} className="bg-sky-500/10 text-sky-200 border-transparent">
                    {c}
                  </Badge>
                ))}
              </div>
            ) : (
              <div className="text-slate-300">— none</div>
            )}
          </div>

          <div>
            <div className="mb-1 text-xs uppercase tracking-wide text-slate-300">
              Dealbreakers
            </div>
            {Object.keys(dealbreak).length > 0 ? (
              <div className="grid grid-cols-2 gap-1 text-xs">
                {Object.entries(dealbreak).map(([k, v]) => (
                  <div key={k} className="rounded-md bg-rose-500/10 px-2 py-1 text-rose-200">
                    <span className="font-medium">{k}:</span> {String(v)}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-slate-300">— none</div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* What happens on publish */}
      <Card data-testid="review-publish-sideeffects">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Rocket className="h-4 w-4 text-slate-200" />
            What happens when you publish
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal pl-5 text-sm text-slate-200 space-y-1">
            <li data-testid="publish-effect-status">
              Status flips <code className="font-mono">draft → active</code>.
            </li>
            <li data-testid="publish-effect-criteria">
              Criteria mirror is regenerated from your role definition. Your
              Step 3 weights &amp; evaluation notes are preserved.
            </li>
            <li data-testid="publish-effect-audit">
              A <code className="font-mono">role.published</code> governance audit row is recorded.
            </li>
            <li data-testid="publish-effect-counter">
              Active-role counter increments if your plan is activated;
              otherwise the publish is audited with{' '}
              <code className="font-mono">plan_unactivated=true</code>.
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* Publish CTA */}
      <div className="flex items-center justify-end">
        <Button
          size="lg"
          disabled={!ready}
          onClick={onPublish}
          data-testid="publish-cta"
        >
          <Check className="mr-2 h-4 w-4" />
          Publish role
        </Button>
      </div>
    </div>
  );
}
